import { SignedIn, SignedOut, SignInButton } from "@clerk/clerk-react";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router";
import { FeaturesSectionWithHoverEffects } from "../components/blocks/feature-section-with-hover-effects";
import { SecuritySection } from "../components/blocks/security-section";
import { Button } from "../components/ui/button";
import FAQs from "../components/ui/faq";
import { FeaturesSectionWithBentoGrid } from "../components/ui/features-section-with-bento";
import { cn } from "../lib/utils";
import * as Paths from "../paths";

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex min-h-screen flex-col w-full bg-white font-sans selection:bg-slate-900/10">
      {/* Navigation */}
      <header
        className={cn(
          "fixed w-full top-0 z-50 transition-all duration-300 border-b border-transparent",
          isScrolled
            ? "bg-white/95 backdrop-blur-md border-slate-100 shadow-sm py-3"
            : "bg-white py-5"
        )}
      >
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Scroll to top"
          >
            <img
              src="/assets/logo.svg"
              alt="AnyLawyer Logo"
              className="w-[140px]"
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {["Features", "Security", "Pricing", "Testimonials", "FAQ"].map(
              (item) => (
                <Link
                  key={item}
                  to={`#${item.toLowerCase()}`}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {item}
                </Link>
              )
            )}
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <SignedIn>
              <Button
                asChild
                className="rounded-lg px-6 bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]"
              >
                <Link to={Paths.app()}>
                  Go to Anylawyer <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <Button
                  variant="ghost"
                  className="rounded-lg text-slate-600 hover:text-slate-900"
                >
                  Log In
                </Button>
              </SignInButton>
              <SignInButton mode="modal">
                <Button className="rounded-lg px-6 bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)] shadow-lg shadow-[rgb(28_28_28)]/20 hover:shadow-[rgb(28_28_28)]/30 transition-all">
                  Start Free Trial
                </Button>
              </SignInButton>
            </SignedOut>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-white border-b shadow-lg p-4 flex flex-col gap-4">
            {["Features", "Security", "Pricing", "Testimonials", "FAQ"].map(
              (item) => (
                <Link
                  key={item}
                  to={`#${item.toLowerCase()}`}
                  className="text-base font-medium text-slate-600 py-2 border-b border-slate-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item}
                </Link>
              )
            )}
            <div className="flex flex-col gap-3 mt-2">
              <SignedOut>
                <SignInButton mode="modal">
                  <Button
                    variant="outline"
                    className="w-full justify-center rounded-lg"
                  >
                    Log In
                  </Button>
                </SignInButton>
                <SignInButton mode="modal">
                  <Button className="w-full justify-center rounded-lg bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]">
                    Start Free Trial
                  </Button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <Button
                  asChild
                  className="w-full justify-center rounded-lg bg-[rgb(28_28_28)] text-white hover:bg-[rgb(20_20_20)]"
                >
                  <Link to={Paths.app()}>Go to Anylawyer</Link>
                </Button>
              </SignedIn>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative w-full py-20 md:py-32 overflow-hidden bg-white">
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-slate-200/20 opacity-20 blur-[100px]"></div>
          </div>

          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
              <div className="flex-1 space-y-8 text-center lg:text-left max-w-2xl lg:max-w-none mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600"
                >
                  <span className="flex h-2 w-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                  New: Adjustable Summary Depth & Reasoning Power
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-5xl md:text-6xl lg:text-7xl font-serif tracking-tight text-slate-900 leading-[1.1]"
                >
                  Legal Intelligence, <br />
                  <span className="italic text-slate-800">Perfected.</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="text-xl text-slate-500 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-sans"
                >
                  The secure AI workspace for high-stakes document analysis.
                  Analyze thousands of documents in minutes. Integrated securely
                  with your private cloud, ensuring zero data training and 100%
                  grounded citations.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 pt-2"
                >
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button
                        size="lg"
                        className="h-12 px-8 text-base font-medium rounded-lg bg-[rgb(28_28_28)] text-white shadow-xl shadow-[rgb(28_28_28)]/10 hover:shadow-[rgb(28_28_28)]/20 transition-all hover:-translate-y-0.5 hover:bg-[rgb(20_20_20)]"
                      >
                        Start Free Trial
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <Button
                      size="lg"
                      asChild
                      className="h-12 px-8 text-base font-medium rounded-lg bg-[rgb(28_28_28)] text-white shadow-xl shadow-[rgb(28_28_28)]/10 hover:shadow-[rgb(28_28_28)]/20 transition-all hover:-translate-y-0.5 hover:bg-[rgb(20_20_20)]"
                    >
                      <Link to={Paths.app()}>
                        Go to Anylawyer
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </SignedIn>
                  <Button
                    variant="outline"
                    size="lg"
                    asChild
                    className="h-12 px-8 text-base font-medium rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <Link to="mailto:hello@anylawyer.com">Book Demo</Link>
                  </Button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  className="pt-6 flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-4 text-sm text-slate-500"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>Fine-tuned Legal Models</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>Zero Data Training</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
                      <Check className="h-3 w-3 text-slate-600" />
                    </div>
                    <span>14-Day Free Trial</span>
                  </div>
                </motion.div>
              </div>

              <div className="flex-1 relative lg:h-[600px] w-full flex items-center justify-center">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="relative w-full aspect-square max-w-[600px]"
                >
                  {/* Abstract UI Representation */}
                  <img
                    src="/assets/side-by-side.png"
                    alt="AnyLawyer Interface"
                    className="relative z-10 w-full rounded-xl shadow-2xl border border-slate-200/60"
                  />
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Trusted By */}
        <section className="border-y border-slate-100 bg-white py-10">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm font-medium text-slate-500 uppercase tracking-widest mb-8">
              Trusted by Leading Law Firms
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-20 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
              {/* Placeholder Logos - keeping the existing one and adding generic placeholders if needed */}
              <img
                src="https://dsk-kancelaria.pl/wp-content/themes/starter-empressia/dist/images/dsk-logo-dark.svg"
                alt="DSK Law Firm"
                className="h-12 object-contain"
              />
              {/* You would add more client logos here */}
            </div>
          </div>
        </section>

        {/* Bento Grid Features */}
        <section id="features" className="py-24 md:py-32 bg-white relative">
          <FeaturesSectionWithBentoGrid />
        </section>

        {/* Additional Capabilities */}
        <section className="py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
                Complete Legal Toolkit
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Everything you need to manage complex legal workflows in one
                secure platform.
              </p>
            </div>
            <FeaturesSectionWithHoverEffects />
          </div>
        </section>

        {/* Security Section */}
        <SecuritySection />

        {/* Pricing Section */}
        <section
          id="pricing"
          className="py-32 bg-slate-50/50 border-t border-slate-100"
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                Choose the plan that best fits your practice&apos;s needs. No
                hidden fees, no long-term commitments.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Professional Plan (Dark) */}
              <div className="bg-[#1c1c1c] rounded-none p-10 border border-gray-800 shadow-xl relative flex flex-col">
                <div className="mb-8">
                  <h3 className="text-2xl font-medium text-white mb-2 font-sans">
                    Professional
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    Advanced features for growing teams and businesses.
                  </p>
                </div>

                <div className="flex flex-col mb-8">
                  <span className="text-6xl font-serif text-white tracking-tight">
                    49€
                  </span>
                  <span className="text-gray-500 text-sm mt-2">
                    per month, per user
                  </span>
                </div>

                <SignedOut>
                  <SignInButton mode="modal">
                    <Button className="w-full h-12 rounded-full text-base font-medium bg-white text-black hover:bg-gray-200 transition-colors mb-10">
                      Get started
                    </Button>
                  </SignInButton>
                </SignedOut>
                <SignedIn>
                  <Button
                    asChild
                    className="w-full h-12 rounded-full text-base font-medium bg-white text-black hover:bg-gray-200 transition-colors mb-10"
                  >
                    <Link to="mailto:hello@anylawyer.com">Get started</Link>
                  </Button>
                </SignedIn>

                <div className="space-y-4 flex-1">
                  {[
                    "Unlimited queries",
                    "Unlimited project workspaces",
                    "SaaS cloud access (EU-hosted)",
                    "Citation-backed answers",
                    "Integrations",
                    "State-of-art fine-tuned models",
                    "Standard email support",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="h-4 w-4 text-white mt-1 shrink-0" />
                      <span className="text-gray-300 text-sm font-light">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enterprise Plan (Light) */}
              <div className="bg-white rounded-none p-10 border border-gray-200 shadow-sm relative flex flex-col">
                <div className="mb-8">
                  <h3 className="text-2xl font-medium text-slate-900 mb-2 font-sans">
                    Enterprise
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed">
                    Complete solution for large organizations and enterprises.
                  </p>
                </div>

                <div className="flex flex-col mb-8">
                  <span className="text-6xl font-sans text-slate-900 tracking-tight">
                    Custom
                  </span>
                  <span className="text-slate-400 text-sm mt-2">
                    contact for pricing
                  </span>
                </div>

                <Button
                  asChild
                  className="w-full h-12 rounded-full text-base font-medium bg-[#1c1c1c] text-white hover:bg-gray-800 transition-colors mb-10"
                >
                  <Link to="mailto:hello@anylawyer.com">Contact sales</Link>
                </Button>

                <div className="space-y-4 flex-1">
                  {[
                    "On-prem / Bring Your Own Cloud",
                    "Dedicated account manager",
                    "SSO (Okta, AD) & Advanced IAM",
                    "Custom SLA & Contracts",
                    "Priority 24/7 support",
                    "Audit logs & Analytics reports",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="h-4 w-4 text-slate-900 mt-1 shrink-0" />
                      <span className="text-slate-600 text-sm font-light">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQs />

        {/* Final CTA */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="bg-[rgb(28_28_28)] p-12 md:p-20 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full z-0">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[rgb(20_20_20)]/30 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[rgb(20_20_20)]/30 rounded-full blur-[100px]"></div>
              </div>
              <div className="relative z-10 max-w-3xl mx-auto space-y-8">
                <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight">
                  Ready to modernize your practice?
                </h2>
                <p className="text-xl text-slate-300">
                  Join the fastest growing legal AI platform today. No credit
                  card required for trial.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <SignedOut>
                    <SignInButton mode="modal">
                      <Button
                        size="lg"
                        className="h-14 px-10 text-lg rounded-lg bg-white hover:bg-slate-100 text-slate-900 font-medium"
                      >
                        Start Free Trial
                      </Button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <Button
                      size="lg"
                      asChild
                      className="h-14 px-10 text-lg rounded-lg bg-white hover:bg-slate-100 text-slate-900 font-medium"
                    >
                      <Link to={Paths.app()}>Go to Anylawyer</Link>
                    </Button>
                  </SignedIn>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-14 px-10 text-lg rounded-lg bg-transparent text-white border-slate-600 hover:bg-slate-800 hover:text-white"
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-100 py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              <div className="col-span-1 md:col-span-1">
                <img src="/assets/logo.svg" alt="Logo" className="w-32 mb-6" />
                <p className="text-slate-500 text-sm">
                  Empowering legal professionals with next-generation AI tools.
                </p>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-4">Product</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li>
                    <Link to="#features" className="hover:text-slate-900">
                      Features
                    </Link>
                  </li>
                  <li>
                    <Link to="#security" className="hover:text-slate-900">
                      Security
                    </Link>
                  </li>
                  <li>
                    <Link to="#" className="hover:text-slate-900">
                      Pricing
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Legal</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link
                      to={Paths.privacyPolicy()}
                      className="hover:text-primary transition-colors"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
                <div className="mt-6">
                  <h3 className="font-semibold mb-2">Contact Us</h3>
                  <Link
                    to="mailto:hello@anylawyer.com"
                    className="text-sm text-primary hover:underline"
                  >
                    hello@anylawyer.com
                  </Link>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-slate-400">
                © 2025 AnyLawyer. All rights reserved.
              </p>
              <div className="flex gap-4">
                {/* Social icons would go here */}
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
